"use client";

import { useState, useTransition } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  Avatar,
  Badge,
  Button,
  Card,
  VerificationBadge,
} from "@/components/ui";
import { IconClock, IconPin } from "@/components/ui/icons";
import { formatMxn, formatSessionTime } from "@/lib/format";
import { acceptBookingAction, declineBookingAction } from "./actions";
import type { Booking, ClientProfile, Locale } from "@/lib/mock";

export interface ClientTrustSignal {
  kind: "rating" | "history" | "new";
  rating?: number;
  count?: number;
}

/**
 * Tarjeta de reserva entrante: nivel de verificación + reputación del cliente
 * visibles ANTES de aceptar (C4). Aceptar/rechazar vía server action
 * (`booking.service.acceptBooking` / `cancelBooking`) — nunca `repo` directo.
 */
export function RequestCard({
  booking,
  client,
  trust,
  locale,
}: {
  booking: Booking;
  client: ClientProfile;
  trust: ClientTrustSignal;
  locale: Locale;
}) {
  const t = useTranslations("pro.dashboard");
  const tc = useTranslations("common");
  const [decision, setDecision] = useState<"pending" | "accepted" | "declined">(
    "pending",
  );
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  if (decision === "declined") return null;

  const handleAccept = () => {
    setError(null);
    startTransition(async () => {
      try {
        await acceptBookingAction({ locale, bookingId: booking.id });
        setDecision("accepted");
      } catch {
        setError(t("actionError"));
      }
    });
  };

  const handleDecline = () => {
    setError(null);
    startTransition(async () => {
      try {
        await declineBookingAction({ locale, bookingId: booking.id });
        setDecision("declined");
      } catch {
        setError(t("actionError"));
      }
    });
  };

  return (
    <Card elevation="raised" data-testid="pro-request-card" data-booking-id={booking.id}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Avatar initials={client.initials} name={client.displayName} size="md" />
          <div>
            <p className="font-medium text-ink">{client.displayName}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <span
                data-testid="request-client-verification"
                data-level={client.verification}
              >
                <VerificationBadge
                  level={client.verification}
                  label={tc(
                    `verification.${client.verification.toLowerCase()}` as
                      | "verification.l0"
                      | "verification.l1"
                      | "verification.l2",
                  )}
                />
              </span>
              {trust.kind === "rating" && trust.rating !== undefined && (
                <Badge variant="clay" dot>
                  {t("clientRating", { rating: trust.rating.toFixed(1) })}
                </Badge>
              )}
              {trust.kind === "history" && (
                <Badge variant="sand" dot>
                  {t("clientHistory", { count: trust.count ?? 0 })}
                </Badge>
              )}
              {trust.kind === "new" && (
                <Badge variant="neutral" dot>
                  {t("clientNew")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <IconClock size={14} />
          {formatSessionTime(booking.startAtUtc, locale)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <IconPin size={14} />
          {tc(`zones.${booking.zoneId}`)}
        </span>
        <span className="nums font-medium text-ink-soft">
          {formatMxn(booking.priceMxn, locale)}
        </span>
      </div>

      {decision === "pending" ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            data-testid="accept-request"
            size="sm"
            onClick={handleAccept}
            disabled={isPending}
          >
            {tc("actions.accept")}
          </Button>
          <Button
            data-testid="decline-request"
            size="sm"
            variant="outline"
            onClick={handleDecline}
            disabled={isPending}
          >
            {tc("actions.decline")}
          </Button>
          <span className="text-xs text-ink-muted">{t("requestsNote")}</span>
          {error && <span className="text-xs text-danger">{error}</span>}
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-3">
          <Badge variant="forest" dot>
            {tc("actions.accept")}
          </Badge>
          <Link
            href={`/pro/session/${booking.id}`}
            className="text-sm font-medium text-forest"
          >
            {t("goToSession")} →
          </Link>
        </div>
      )}
    </Card>
  );
}
